import type { Observable } from 'rxjs'
import { env } from '@/infra/env'
import type { CursorPaged } from '@/infra/paging'
import { FreshbooksApi } from '@/features/freshbooks/api/freshbooks-api'
import type { CallCenterContact } from '@/features/freshbooks/CallCenterContact'
import {
  CallCenterContactStore,
  type SearchCallCenterContactsQuery,
} from '@/features/freshbooks/CallCenterContactStore'
import { HubspotTokenStore } from '@/features/freshbooks/HubspotTokenStore'

/**
 * Options for {@link FreshbooksFeature}.
 */
export interface FreshbooksFeatureOptions {
  token$: Observable<string>
}

/**
 * Searches the call center's contacts in Freshbooks.
 */
export type SearchCallCenterContactsFn = (
  query: SearchCallCenterContactsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<CallCenterContact>>

/**
 * Fetches the token that identifies the current user to the HubSpot chat widget.
 */
export type FetchHubspotTokenFn = (signal?: AbortSignal) => Promise<string>

/**
 * Freshbooks integrations: call center contact search and HubSpot identification.
 */
export type FreshbooksFeature = ReturnType<typeof FreshbooksFeature>

export function FreshbooksFeature(options: FreshbooksFeatureOptions) {
  const freshbooksApi = new FreshbooksApi({
    baseURL: env.FRESHBOOKS_API_EXTERNAL,
    token$: options.token$,
  })

  const callCenterContactStore = new CallCenterContactStore(
    freshbooksApi.callCenter,
  )
  const hubspotTokenStore = new HubspotTokenStore(freshbooksApi.hubspot)

  const searchCallCenterContacts: SearchCallCenterContactsFn = (
    query,
    signal,
  ) => callCenterContactStore.searchCallCenterContacts(query, signal)

  const fetchHubspotToken: FetchHubspotTokenFn = (signal) =>
    hubspotTokenStore.fetchToken(signal)

  return {
    // Public
    searchCallCenterContacts,
    fetchHubspotToken,
  }
}
